import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, IconButton } from "@mui/material";
import InfiniteScroll from "react-infinite-scroller";
import { useFormik } from "formik";
import { object, string } from "yup";
import { useDispatch, useSelector } from "react-redux";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { setNewAddressObj, setSendToContactData } from "../../../redux/actions";
import BackdropLoader from "../../backdropLoader/BackdropLoader";
import {
  addNewAddress,
  failed,
  loadingMsg,
  noTransactions,
  noTransactionsSubMsg,
  send,
  sent,
} from "../../messages";
import {
  callAPIInterface,
  contactPaymentMethods,
  contactTypes,
  dateTimeFormat,
  displayTaprootCurrency,
  existingContactLabel,
  getAmountFormatted,
  getCurrencyAmount,
  getIcon,
  noOfRecords,
} from "../../constants";
import Button from "../../button/Button";
import Text from "../../text/Text";
import { failedIcon, sentIcon, timerIconGrey } from "../../images";
import ErrorMessageContent from "../../ErrorMessageContent";
import ListItemSkeleton from "../../ListItemSkeleton";
import CommonHeader from "../../CommonHeader";
import TruncatedTextTooltip from "../../TruncatedTextTooltip";
import { useSession } from "../../../hooks/useSession";
import CustomDivider from "../../divider/Divider";
import AddressListDrawer from "./AddressListDrawer";
import AddContactDrawer from "./AddContactDrawer";

const withdrawObj = {
  paid: {
    icon: sentIcon,
    text: sent,
    txtColor: "#FD371E",
  },
  failed: {
    icon: failedIcon,
    text: failed,
    txtColor: "#929292",
  },
};

const validationSchema = object({
  name: string().required(),
  customer_type: string().required(),
  payment_address: string().required(),
  payment_method: string().required(),
});

function ContactDetails() {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const newAddressObj = useSelector((state) => state.common.newAddressObj);
  const { session } = useSession();

  const userDateFormat = session?.user?.date_format || "DD/MM/YYYY";
  const [isLoading, setIsLoading] = useState(true);
  const [contact, setContact] = useState();
  const [openAddressList, setOpenAddressList] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [openAddDrawer, setOpenAddDrawer] = useState(false);
  const [withdraws, setWithdraws] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);

  const { id } = params;
  const limitParam = `?limit=${noOfRecords}`;

  const getInputValues = (values) => {
    const { payment_address, payment_method } = values;

    const paymentMethodObj = contactPaymentMethods.find(
      (method) => method.label === payment_method
    );

    return paymentMethodObj
      ? {
          payment_address_details: [
            {
              payment_address,
              payment_method: paymentMethodObj.payment_method,
              target_currency: paymentMethodObj.target_currency,
            },
          ],
        }
      : null;
  };

  const handleSend = () => {
    dispatch(
      setSendToContactData({
        payment_address: selectedAddress?.payment_address,
        payment_address_id: selectedAddress?.id,
      })
    );

    navigate("/wallet");
  };

  const getWithdraws = async (withdraws, address, params) => {
    try {
      const apiData = {
        contact_payment_address_ids: [`${address?.id}`],
      };

      const res = await callAPIInterface(
        "POST",
        `/send/filter${params}`,
        apiData
      );

      setWithdraws(withdraws.concat(res?.data));
      setHasMore(res?.has_more);
      setIsFetching(false);
    } catch (error) {
      console.error(error);
    }
  };

  const loadMore = useCallback(() => {
    const params = `${limitParam}&ending_before=${
      withdraws[withdraws?.length - 1].id
    }`;

    hasMore && getWithdraws(withdraws, selectedAddress, params);
  }, [withdraws, selectedAddress]);

  const handleAddAddress = async (val) => {
    setButtonLoader(true);
    try {
      const apiDataObj = getInputValues(val);
      if (apiDataObj) {
        await callAPIInterface(
          "POST",
          `/customers/${id}/payment-addresses`,
          apiDataObj
        );
        setOpenAddDrawer(false);
        getContactDetails();
      }
    } catch (error) {
      console.error("Error adding Address", error);
    } finally {
      setButtonLoader(false);
      resetForm();
    }
  };

  const formik = useFormik({
    initialValues: {
      customer_type: contactTypes.find(
        (type) => type.value === contact?.customer_type
      )?.label,
      name: contact?.name,
      payment_address: "",
      payment_method: "",
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: handleAddAddress,
  });

  const getContactDetails = async () => {
    try {
      const res = await callAPIInterface("GET", `/customers/${id}`);
      setContact(res);
      setSelectedAddress(res?.payment_address_details[0]);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
    }
  };

  const handleOpenDetailPage = (transaction) => {
    localStorage.setItem(
      "transactionDetail",
      JSON.stringify({
        type: transaction?.object,
        id: transaction?.withdraw_id,
        confirmed: true,
        transactionId: transaction.id,
        netAmount: transaction?.amount,
      })
    );

    navigate(`/history/${transaction.withdraw_id}`);
  };

  const transactionItem = useCallback((withdraw, index) => {
    const finalAmount = getCurrencyAmount(
      withdraw?.target_amount,
      withdraw?.target_currency
    );

    const { icon, text, txtColor } = withdrawObj[withdraw?.status] ?? {};

    return (
      <Box
        className="transaction"
        key={withdraw.id}
        onClick={() => handleOpenDetailPage(withdraw)}
        sx={{
          cursor: "pointer",
          borderBottom:
            withdraws?.length - 1 === index ? "none" : "1px solid #f6f3f3",
        }}
      >
        <Box className="left-side">
          <img src={icon} height={30} width={30} />
          <Box ml="14px">
            <Text className="grey-text" size={12} font="regular">
              {dateTimeFormat(withdraw?.created, userDateFormat)}
            </Text>
            <Text className="default-text" size={16} font="semibold">
              {text}
            </Text>
          </Box>
        </Box>
        <Box className="right-side" textAlign="right">
          <Box ml="14px">
            <Text
              className="grey-text"
              size={16}
              font="bold"
              sx={{
                color: `${txtColor} !important`,
              }}
            >
              {`${finalAmount === 0 ? "" : "-"} ${getAmountFormatted(
                finalAmount,
                withdraw?.target_currency
              )}`}
            </Text>
            <Text className="default-text" size={14} font="semibold">
              {displayTaprootCurrency(finalAmount, withdraw.target_currency)}
            </Text>
          </Box>
        </Box>
      </Box>
    );
  }, []);

  const renderTransactionList = useCallback(() => {
    if (!withdraws.length && !hasMore) {
      return (
        <Box className="no-transaction-section">
          <ErrorMessageContent
            msg={noTransactions}
            subMsg={noTransactionsSubMsg}
            img={timerIconGrey}
            padding={true}
          />
        </Box>
      );
    }

    return (
      <InfiniteScroll
        pageStart={0}
        loadMore={loadMore}
        initialLoad={true}
        hasMore={hasMore}
        loader={<ListItemSkeleton />}
        useWindow={false}
      >
        {withdraws.map((withdraw, index) => transactionItem(withdraw, index))}
      </InfiniteScroll>
    );
  }, [withdraws]);

  const renderSelectedAddress = useCallback((selectedAddress) => {
    const { payment_method, target_currency } = selectedAddress;
    const imageUrl = getIcon(payment_method, target_currency);

    const addressType = contactPaymentMethods?.find(
      (method) => method.payment_method === payment_method
    )?.label;

    return (
      <Box
        className="address-list-item"
        onClick={() => setOpenAddressList(true)}
      >
        <img src={imageUrl} height={30} width={30} />

        <Box display="flex" flexDirection="column">
          <Text size={16} font="semibold">
            {addressType}
          </Text>
          <TruncatedTextTooltip
            textProps={{
              className: "grey-text",
              font: "regular",
              width: {
                xs: "200px",
                sm: "350px",
                md: "400px",
              },
            }}
            textValue={selectedAddress?.payment_address}
            fontSize={14}
          />
        </Box>
        <IconButton>
          <KeyboardArrowDownIcon />
        </IconButton>
      </Box>
    );
  }, []);

  const addressListProps = {
    openDrawer: openAddressList,
    contact,
    setOpenAddDrawer,
    setContact,
    setOpenDrawer: setOpenAddressList,
    setDisplayedAddress: setSelectedAddress,
  };

  const { setFieldValue, resetForm } = formik;

  useEffect(() => {
    getContactDetails();
  }, [id]);

  useEffect(() => {
    if (newAddressObj && contact) {
      const { withdraw_request, type } = newAddressObj;
      if (withdraw_request && type === existingContactLabel) {
        setFieldValue("payment_address", withdraw_request);
        setOpenAddDrawer(true);
        dispatch(setNewAddressObj({}));
      }
    }
  }, [newAddressObj, contact]);

  useEffect(() => {
    if (selectedAddress) {
      setIsFetching(true);
      getWithdraws([], selectedAddress, limitParam);
    }
  }, [selectedAddress]);

  return isLoading ? (
    <BackdropLoader
      open={isLoading}
      alt={loadingMsg}
      text={loadingMsg}
      customClass="loading-in-auth"
    />
  ) : (
    <>
      <CommonHeader
        textAfterIcon={contact?.name}
        icon="backArrow"
        navigation="/settings/contacts"
      />
      <Box className="contact-details-container">
        <Box>{selectedAddress && renderSelectedAddress(selectedAddress)}</Box>
        <Box className="advance-settings-wrapper contact-details-wrapper">
          <Box className="module-container address-list-transactions">
            <Box
              className="module-container-content"
              paddingBottom="12px"
              position="relative"
            >
              {isFetching ? <ListItemSkeleton /> : renderTransactionList()}
            </Box>
            <Box className="module-container-footer">
              <CustomDivider className="settings-divider" />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                label={send}
                onClick={handleSend}
              />
            </Box>
          </Box>
          <AddressListDrawer {...addressListProps} />
          <AddContactDrawer
            formik={formik}
            modalHeader={addNewAddress}
            buttonLoader={buttonLoader}
            setButtonLoader={setButtonLoader}
            disabledFields={["payment_method", "name", "customer_type"]}
            openDrawer={openAddDrawer}
            setOpenDrawer={setOpenAddDrawer}
          />
        </Box>
      </Box>
    </>
  );
}

export default ContactDetails;
